import { Injectable } from '@angular/core';
import { FormGroup, AbstractControl } from '@angular/forms';

import { 
  CustomerFormData,
  ValidationErrors,
  VALIDATION_RULES,
  FormStep,
  RegionFormData,
  SiteFormData,
  UserFormData
} from '../models';

/**
 * Service for form validation across the customer onboarding flow
 * Provides validation methods for all form steps and data types
 */
@Injectable({
  providedIn: 'root'
})
export class ValidationService {

  constructor() { }

  /**
   * Validate complete customer form data
   * @param formData - Complete form data
   * @returns ValidationErrors object
   */
  validateCustomerForm(formData: CustomerFormData): ValidationErrors {
    const errors: ValidationErrors = {};

    // Validate customer info
    const customerErrors = this.validateCustomer(formData.customer);
    if (Object.keys(customerErrors).length > 0) {
      errors.customer = customerErrors;
    }

    // Validate customer profile
    const profileErrors = this.validateCustomerProfile(formData.customerProfile);
    if (Object.keys(profileErrors).length > 0) {
      errors.customerProfile = profileErrors;
    }

    // Validate regions
    const regionErrors = this.validateRegions(formData.regions);
    if (Object.keys(regionErrors).length > 0) {
      errors.regions = regionErrors;
    }

    // Validate sites
    const siteErrors = this.validateSites(formData.sites, formData.regions);
    if (Object.keys(siteErrors).length > 0) {
      errors.sites = siteErrors;
    }

    // Validate admin users
    const adminUserErrors = this.validateUsers(formData.adminUsers);
    if (Object.keys(adminUserErrors).length > 0) {
      errors.adminUsers = adminUserErrors;
    }

    // Validate operator users
    const operatorUserErrors = this.validateUsers(formData.operatorUsers);
    if (Object.keys(operatorUserErrors).length > 0) {
      errors.operatorUsers = operatorUserErrors;
    }

    // General validation
    const generalErrors = this.validateGeneral(formData);
    if (generalErrors.length > 0) {
      errors.general = generalErrors;
    }

    return errors;
  }

  /**
   * Validate customer basic information
   * @param customer - Customer data
   * @returns Object with field-specific errors
   */
  validateCustomer(customer: any): { [key: string]: string } {
    const errors: { [key: string]: string } = {};

    // Customer name validation
    if (!customer.name || customer.name.trim().length === 0) {
      errors.name = 'Customer name is required';
    } else if (customer.name.length < VALIDATION_RULES.CUSTOMER_NAME.minLength) {
      errors.name = `Customer name must be at least ${VALIDATION_RULES.CUSTOMER_NAME.minLength} characters`;
    } else if (customer.name.length > VALIDATION_RULES.CUSTOMER_NAME.maxLength) {
      errors.name = `Customer name must not exceed ${VALIDATION_RULES.CUSTOMER_NAME.maxLength} characters`;
    }

    // Contact number validation
    if (!customer.contactNo || customer.contactNo.trim().length === 0) {
      errors.contactNo = 'Contact number is required';
    } else if (!VALIDATION_RULES.CONTACTNO.pattern.test(customer.contactNo)) {
      errors.contactNo = 'Please enter a valid contact number';
    }

    // Email validation
    if (!customer.email || customer.email.trim().length === 0) {
      errors.email = 'Email is required';
    } else if (!VALIDATION_RULES.EMAIL.pattern.test(customer.email)) {
      errors.email = 'Please enter a valid email address';
    }

    return errors;
  }

  /**
   * Validate customer profile information
   * @param profile - Customer profile data
   * @returns Object with field-specific errors
   */
  validateCustomerProfile(profile: any): { [key: string]: string } {
    const errors: { [key: string]: string } = {};

    if (!profile.name || profile.name.trim().length === 0) {
      errors.name = 'Customer profile name is required';
    } else if (profile.name.length < 2) {
      errors.name = 'Profile name must be at least 2 characters';
    } else if (profile.name.length > 100) {
      errors.name = 'Profile name must not exceed 100 characters';
    }

    return errors;
  }

  /**
   * Validate regions array
   * @param regions - Array of region form data
   * @returns Object with index-based errors
   */
  validateRegions(regions: RegionFormData[]): { [index: number]: { [key: string]: string } } {
    const errors: { [index: number]: { [key: string]: string } } = {};

    if (regions.length === 0) {
      errors[0] = { general: 'At least one region is required' };
      return errors;
    }

    regions.forEach((region, index) => {
      const regionErrors = this.validateRegion(region, regions, index);
      if (Object.keys(regionErrors).length > 0) {
        errors[index] = regionErrors;
      }
    });

    return errors;
  }

  /**
   * Validate single region
   * @param region - Region form data
   * @param allRegions - All regions for uniqueness check
   * @param currentIndex - Current region index for uniqueness check
   * @returns Object with field-specific errors
   */
  validateRegion(region: RegionFormData, allRegions: RegionFormData[], currentIndex: number): { [key: string]: string } {
    const errors: { [key: string]: string } = {};

    // Name validation
    if (!region.name || region.name.trim().length === 0) {
      errors.name = 'Region name is required';
    } else if (region.name.length < VALIDATION_RULES.REGION_NAME.minLength) {
      errors.name = `Region name must be at least ${VALIDATION_RULES.REGION_NAME.minLength} characters`;
    } else if (region.name.length > VALIDATION_RULES.REGION_NAME.maxLength) {
      errors.name = `Region name must not exceed ${VALIDATION_RULES.REGION_NAME.maxLength} characters`;
    }

    // Uniqueness validation
    if (region.name) {
      const duplicateIndex = allRegions.findIndex((r, index) => 
        index !== currentIndex && r.name.toLowerCase() === region.name.toLowerCase()
      );
      if (duplicateIndex !== -1) {
        errors.name = 'Region name must be unique';
      }
    }

    return errors;
  }

  /**
   * Validate sites array
   * @param sites - Array of site form data
   * @param regions - Array of regions for validation
   * @returns Object with index-based errors
   */
  validateSites(sites: SiteFormData[], regions: RegionFormData[]): { [index: number]: { [key: string]: string } } {
    const errors: { [index: number]: { [key: string]: string } } = {};

    if (sites.length === 0) {
      errors[0] = { general: 'At least one site is required' };
      return errors;
    }

    sites.forEach((site, index) => {
      const siteErrors = this.validateSite(site, sites, regions, index);
      if (Object.keys(siteErrors).length > 0) {
        errors[index] = siteErrors;
      }
    });

    return errors;
  }

  /**
   * Validate single site
   * @param site - Site form data
   * @param allSites - All sites for uniqueness check
   * @param regions - All regions for reference validation
   * @param currentIndex - Current site index for uniqueness check
   * @returns Object with field-specific errors
   */
  validateSite(site: SiteFormData, allSites: SiteFormData[], regions: RegionFormData[], currentIndex: number): { [key: string]: string } {
    const errors: { [key: string]: string } = {};

    // Name validation
    if (!site.name || site.name.trim().length === 0) {
      errors.name = 'Site name is required';
    } else if (site.name.length < VALIDATION_RULES.SITE_NAME.minLength) {
      errors.name = `Site name must be at least ${VALIDATION_RULES.SITE_NAME.minLength} characters`;
    } else if (site.name.length > VALIDATION_RULES.SITE_NAME.maxLength) {
      errors.name = `Site name must not exceed ${VALIDATION_RULES.SITE_NAME.maxLength} characters`;
    }

    // Region validation
    if (!site.region_id) {
      errors.region = 'Region is required for site';
    } else {
      const regionExists = regions.some(r => r.id === site.region_id);
      if (!regionExists) {
        errors.region = 'Selected region does not exist';
      }
    }

    // Uniqueness validation within region
    if (site.name && site.region_id) {
      const duplicateIndex = allSites.findIndex((s, index) => 
        index !== currentIndex && 
        s.region_id === site.region_id && 
        s.name.toLowerCase() === site.name.toLowerCase()
      );
      if (duplicateIndex !== -1) {
        errors.name = 'Site name must be unique within region';
      }
    }

    return errors;
  }

  /**
   * Validate users array
   * @param users - Array of user form data
   * @returns Object with index-based errors
   */
  validateUsers(users: UserFormData[]): { [index: number]: { [key: string]: string } } {
    const errors: { [index: number]: { [key: string]: string } } = {};

    users.forEach((user, index) => {
      const userErrors = this.validateUser(user, users, index);
      if (Object.keys(userErrors).length > 0) {
        errors[index] = userErrors;
      }
    });

    return errors;
  }

  /**
   * Validate single user
   * @param user - User form data
   * @param allUsers - All users for uniqueness check
   * @param currentIndex - Current user index for uniqueness check
   * @returns Object with field-specific errors
   */
  validateUser(user: UserFormData, allUsers: UserFormData[], currentIndex: number): { [key: string]: string } {
    const errors: { [key: string]: string } = {};

    // Username validation
    if (!user.username || user.username.trim().length === 0) {
      errors.username = 'Username is required';
    } else if (user.username.length < VALIDATION_RULES.USERNAME.minLength) {
      errors.username = `Username must be at least ${VALIDATION_RULES.USERNAME.minLength} characters`;
    } else if (user.username.length > VALIDATION_RULES.USERNAME.maxLength) {
      errors.username = `Username must not exceed ${VALIDATION_RULES.USERNAME.maxLength} characters`;
    } else if (!VALIDATION_RULES.USERNAME.pattern.test(user.username)) {
      errors.username = 'Username can only contain letters, numbers, and underscores';
    }

    // Password validation (for new users or when password is being changed)
    if (user.isNew || (user.password && user.password.length > 0)) {
      if (!user.password || user.password.length === 0) {
        errors.password = 'Password is required';
      } else if (user.password.length < VALIDATION_RULES.PASSWORD.minLength) {
        errors.password = `Password must be at least ${VALIDATION_RULES.PASSWORD.minLength} characters`;
      } else if (user.password.length > VALIDATION_RULES.PASSWORD.maxLength) {
        errors.password = `Password must not exceed ${VALIDATION_RULES.PASSWORD.maxLength} characters`;
      }

      // Confirm password validation
      if (user.confirmPassword !== user.password) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    // User name validation
    if (!user.userDetails.name || user.userDetails.name.trim().length === 0) {
      errors.name = 'User name is required';
    } else if (user.userDetails.name.length < VALIDATION_RULES.USER_NAME.minLength) {
      errors.name = `User name must be at least ${VALIDATION_RULES.USER_NAME.minLength} characters`;
    } else if (user.userDetails.name.length > VALIDATION_RULES.USER_NAME.maxLength) {
      errors.name = `User name must not exceed ${VALIDATION_RULES.USER_NAME.maxLength} characters`;
    }

    // Email validation (optional but must be valid if provided)
    if (user.userDetails.email && user.userDetails.email.trim().length > 0) {
      if (!VALIDATION_RULES.EMAIL.pattern.test(user.userDetails.email)) {
        errors.email = 'Please enter a valid email address';
      }
    }

    // Role validation
    if (!user.roleIds || user.roleIds.length === 0) {
      errors.roles = 'At least one role is required';
    }

    // Assignment validation based on user type
    if (user.userType === 'admin') {
      if (!user.regionIds || user.regionIds.length === 0) {
        errors.regions = 'Admin users must be assigned to at least one region';
      }
    } else if (user.userType === 'operator') {
      if (!user.siteIds || user.siteIds.length === 0) {
        errors.sites = 'Operator users must be assigned to at least one site';
      }
    }

    // Username uniqueness validation
    if (user.username) {
      const duplicateIndex = allUsers.findIndex((u, index) => 
        index !== currentIndex && u.username.toLowerCase() === user.username.toLowerCase()
      );
      if (duplicateIndex !== -1) {
        errors.username = 'Username must be unique';
      }
    }

    return errors;
  }

  /**
   * Validate general form requirements
   * @param formData - Complete form data
   * @returns Array of general errors
   */
  validateGeneral(formData: CustomerFormData): string[] {
    const errors: string[] = [];

    // Check minimum requirements
    if (formData.adminUsers.length === 0) {
      errors.push('At least one admin user is required');
    }

    // Check region-site relationship
    formData.regions.forEach(region => {
      const sitesInRegion = formData.sites.filter(site => site.region_id === region.id);
      if (sitesInRegion.length === 0) {
        errors.push(`Region "${region.name}" must have at least one site`);
      }
    });

    // Check user assignments consistency
    formData.adminUsers.forEach(admin => {
      admin.regionIds.forEach(regionId => {
        const regionExists = formData.regions.some(r => r.id === regionId);
        if (!regionExists) {
          errors.push(`Admin "${admin.username}" is assigned to a non-existent region`);
        }
      });
    });

    formData.operatorUsers.forEach(operator => {
      operator.siteIds.forEach(siteId => {
        const siteExists = formData.sites.some(s => s.id === siteId);
        if (!siteExists) {
          errors.push(`Operator "${operator.username}" is assigned to a non-existent site`);
        }
      });
    });

    return errors;
  }

  /**
   * Validate specific form step
   * @param step - Form step to validate
   * @param formData - Complete form data
   * @returns ValidationErrors for the step
   */
  validateStep(step: FormStep, formData: CustomerFormData): ValidationErrors {
    const errors: ValidationErrors = {};

    switch (step) {
      case FormStep.CUSTOMER_INFO:
        const customerErrors = this.validateCustomer(formData.customer);
        if (Object.keys(customerErrors).length > 0) {
          errors.customer = customerErrors;
        }
        
        const profileErrors = this.validateCustomerProfile(formData.customerProfile);
        if (Object.keys(profileErrors).length > 0) {
          errors.customerProfile = profileErrors;
        }
        break;

      case FormStep.REGIONS:
        const regionErrors = this.validateRegions(formData.regions);
        if (Object.keys(regionErrors).length > 0) {
          errors.regions = regionErrors;
        }
        break;

      case FormStep.SITES:
        const siteErrors = this.validateSites(formData.sites, formData.regions);
        if (Object.keys(siteErrors).length > 0) {
          errors.sites = siteErrors;
        }
        break;

      case FormStep.USERS:
        const adminUserErrors = this.validateUsers(formData.adminUsers);
        if (Object.keys(adminUserErrors).length > 0) {
          errors.adminUsers = adminUserErrors;
        }
        
        const operatorUserErrors = this.validateUsers(formData.operatorUsers);
        if (Object.keys(operatorUserErrors).length > 0) {
          errors.operatorUsers = operatorUserErrors;
        }
        break;
    }

    return errors;
  }

  /**
   * Check if form step is valid
   * @param step - Form step to check
   * @param formData - Complete form data
   * @returns True if step is valid
   */
  isStepValid(step: FormStep, formData: CustomerFormData): boolean {
    const stepErrors = this.validateStep(step, formData);
    return Object.keys(stepErrors).length === 0;
  }

  /**
   * Get validation summary
   * @param formData - Complete form data
   * @returns Summary of validation results
   */
  getValidationSummary(formData: CustomerFormData): {
    isValid: boolean;
    totalErrors: number;
    stepResults: Array<{ step: FormStep; isValid: boolean; errorCount: number }>;
  } {
    const allErrors = this.validateCustomerForm(formData);
    const totalErrors = this.countErrors(allErrors);

    const stepResults = [
      FormStep.CUSTOMER_INFO,
      FormStep.REGIONS,
      FormStep.SITES,
      FormStep.USERS
    ].map(step => {
      const stepErrors = this.validateStep(step, formData);
      return {
        step,
        isValid: Object.keys(stepErrors).length === 0,
        errorCount: this.countErrors(stepErrors)
      };
    });

    return {
      isValid: totalErrors === 0,
      totalErrors,
      stepResults
    };
  }

  /**
   * Count total number of errors in ValidationErrors object
   * @param errors - ValidationErrors object
   * @returns Number of errors
   */
  private countErrors(errors: ValidationErrors): number {
    let count = 0;
    
    Object.keys(errors).forEach(key => {
      const value = errors[key as keyof ValidationErrors];
      if (Array.isArray(value)) {
        count += value.length;
      } else if (typeof value === 'object' && value !== null) {
        Object.keys(value).forEach(subKey => {
          if (typeof value[subKey] === 'object') {
            count += Object.keys(value[subKey]).length;
          } else {
            count++;
          }
        });
      }
    });
    
    return count;
  }
}